import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  runTransaction,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { Order, PrintAck, PrintClaim } from '../../types'
import { getFirestoreInstance } from '../firebase'
import { decidePrintClaimWin } from '../printClaim'
import { getCurrentPlaceId } from '../placeConfig'
import {
  mergeOrdersWithPendingPatches,
  mergePendingPatches,
  removePrintClaimFromPendingPatch,
  resolveOrderForPersist,
  type PendingOrderPatch,
} from './ordersOptimistic'
import { getBills } from './bills'
import {
  LIVE_ORDER_STATUSES,
  businessDayIsoBounds,
  liveStartIso,
  monthKeyBounds,
  nowMs,
} from './liveCacheWindow'
import {
  LIVE_UPDATED_AT_FIELD,
  mergeByServerUpdateTime,
  versionedRowFromSnap,
  type VersionedRow,
} from './mergeByServerUpdateTime'
import { recordLiveCounts, recordLiveStartIso, recordSnapshotApplyMs } from './perfPulse'

const ORDERS_UPDATED_EVENT = 'wortelogy-orders-updated'

const ordersCacheByPlace: Record<string, Order[]> = {}
const counterCacheByPlace: Record<string, number> = {}
const pendingPatchesByPlace: Record<string, Map<string, PendingOrderPatch>> = {}
const historyOrdersByPlace: Record<string, Map<string, Order>> = {}
let ordersUnsubscribes: (() => void)[] = []
let liveKitchenRows: VersionedRow<Order>[] | null = null
let liveRecentRows: VersionedRow<Order>[] | null = null
let liveBackfillRows: VersionedRow<Order>[] = []
let subscribedLiveStartIso = ''
let lastOrderBackfillKey = ''

function getPendingMap(placeId: string): Map<string, PendingOrderPatch> {
  if (!pendingPatchesByPlace[placeId]) {
    pendingPatchesByPlace[placeId] = new Map()
  }
  return pendingPatchesByPlace[placeId]
}

function applySnapshotToCache(placeId: string, serverOrders: Order[]): void {
  const previous = ordersCacheByPlace[placeId]
  const pending = getPendingMap(placeId)
  const { orders, pending: nextPending } = mergeOrdersWithPendingPatches(
    serverOrders,
    pending,
    previous,
  )
  ordersCacheByPlace[placeId] = orders
  pendingPatchesByPlace[placeId] = nextPending
}

function mergeOrdersById(...lists: Order[][]): Order[] {
  const map = new Map<string, Order>()
  for (const list of lists) {
    for (const order of list) map.set(order.id, order)
  }
  return [...map.values()]
}

function historyMap(placeId: string): Map<string, Order> {
  if (!historyOrdersByPlace[placeId]) historyOrdersByPlace[placeId] = new Map()
  return historyOrdersByPlace[placeId]
}

function replaceHistoryOrders(placeId: string, rows: Order[]): void {
  const hist = new Map<string, Order>()
  for (const order of rows) hist.set(order.id, order)
  historyOrdersByPlace[placeId] = hist
}

function orderFromSnap(d: QueryDocumentSnapshot): VersionedRow<Order> {
  return versionedRowFromSnap(d, (id, fields) => ({ id, ...fields }) as Order)
}

function emitLiveOrders(placeId: string): void {
  if (liveKitchenRows === null || liveRecentRows === null) return
  const started = nowMs()
  const live = mergeByServerUpdateTime(liveKitchenRows, liveRecentRows, liveBackfillRows)
  applySnapshotToCache(placeId, live)
  recordSnapshotApplyMs(nowMs() - started)
  recordLiveCounts({ orders: (ordersCacheByPlace[placeId] ?? []).length })
  dispatchOrdersUpdated()
}

function stopOrderListeners(): void {
  for (const unsub of ordersUnsubscribes) unsub()
  ordersUnsubscribes = []
  liveKitchenRows = null
  liveRecentRows = null
  liveBackfillRows = []
  lastOrderBackfillKey = ''
}

function dispatchOrdersUpdated(): void {
  window.dispatchEvent(new CustomEvent(ORDERS_UPDATED_EVENT))
}

// Remove all undefined values recursively so Firestore setDoc never receives them.
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) =>
      v && typeof v === 'object' ? stripUndefined(v as any) : v,
    ) as unknown as T
  }
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue
      if (Array.isArray(v) || (v && typeof v === 'object')) {
        result[key] = stripUndefined(v as any)
      } else {
        result[key] = v
      }
    }
    return result as T
  }
  return value
}

function ordersRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'orders')
}

function orderDoc(placeId: string, orderId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'orders', orderId)
}

function countersOrdersRef(placeId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'counters', 'orders')
}

const IN_BATCH = 30

async function fetchOrdersByBillIds(placeId: string, billIds: string[]): Promise<VersionedRow<Order>[]> {
  const unique = [...new Set(billIds.filter(Boolean))]
  if (unique.length === 0) return []
  const ref = ordersRef(placeId)
  const out: VersionedRow<Order>[] = []
  for (let i = 0; i < unique.length; i += IN_BATCH) {
    const chunk = unique.slice(i, i + IN_BATCH)
    const snap = await getDocs(query(ref, where('billId', 'in', chunk)))
    for (const d of snap.docs) out.push(orderFromSnap(d))
  }
  return out
}

export async function fetchOrdersForBillIds(billIds: string[]): Promise<Order[]> {
  const rows = await fetchOrdersByBillIds(getCurrentPlaceId(), billIds)
  return rows.map((row) => row.data)
}

/** Load order documents by id. Used when a bill lists tickets whose `billId` points elsewhere. */
export async function fetchOrdersByIds(ids: readonly string[]): Promise<Order[]> {
  const placeId = getCurrentPlaceId()
  const unique = [...new Set(ids.filter((id) => id.trim() !== ''))]
  const out: Order[] = []
  for (let i = 0; i < unique.length; i += 20) {
    const chunk = unique.slice(i, i + 20)
    const snaps = await Promise.all(chunk.map((id) => getDoc(orderDoc(placeId, id))))
    for (const snap of snaps) {
      if (!snap.exists()) continue
      out.push(versionedRowFromSnap(snap, (id, fields) => ({ id, ...fields }) as Order).data)
    }
  }
  return out
}

async function backfillOpenBillOrders(placeId: string): Promise<boolean> {
  const startIso = liveStartIso()
  const have = new Set(
    mergeByServerUpdateTime(liveKitchenRows ?? [], liveRecentRows ?? []).map((o) => o.id),
  )
  const liveBillIds = getBills()
    .filter((b) => b.paidAt == null || b.paidAt >= startIso)
    .map((b) => b.id)
    .sort()
  const key = liveBillIds.join(',')
  if (key === lastOrderBackfillKey) return false
  lastOrderBackfillKey = key
  if (liveBillIds.length === 0) {
    liveBackfillRows = []
    return true
  }
  const extra = await fetchOrdersByBillIds(placeId, liveBillIds)
  liveBackfillRows = extra.filter((o) => !have.has(o.id))
  return true
}

export async function refreshOpenBillOrderBackfill(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (liveKitchenRows === null || liveRecentRows === null) return
  const changed = await backfillOpenBillOrders(placeId)
  if (changed) emitLiveOrders(placeId)
}

function bindOrderListeners(placeId: string, startIso: string): void {
  const ref = ordersRef(placeId)
  const kitchenQuery = query(ref, where('status', 'in', [...LIVE_ORDER_STATUSES]))
  const recentQuery = query(ref, where('datetime', '>=', startIso))
  ordersUnsubscribes.push(
    onSnapshot(
      kitchenQuery,
      (snapshot) => {
        liveKitchenRows = snapshot.docs.map((d) => orderFromSnap(d))
        emitLiveOrders(placeId)
      },
      (err) => console.error('[orders] kitchen subscribe failed', err),
    ),
    onSnapshot(
      recentQuery,
      (snapshot) => {
        liveRecentRows = snapshot.docs.map((d) => orderFromSnap(d))
        emitLiveOrders(placeId)
      },
      (err) => console.error('[orders] recent subscribe failed', err),
    ),
  )
}

export async function primeOrdersCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  stopOrderListeners()
  const startIso = liveStartIso()
  subscribedLiveStartIso = startIso
  recordLiveStartIso(startIso)
  const ref = ordersRef(placeId)
  const kitchenQuery = query(ref, where('status', 'in', [...LIVE_ORDER_STATUSES]))
  const recentQuery = query(ref, where('datetime', '>=', startIso))
  const [kitchenSnap, recentSnap, counterSnap] = await Promise.all([
    getDocs(kitchenQuery),
    getDocs(recentQuery),
    getDoc(countersOrdersRef(placeId)),
  ])
  liveKitchenRows = kitchenSnap.docs.map((d) => orderFromSnap(d))
  liveRecentRows = recentSnap.docs.map((d) => orderFromSnap(d))
  counterCacheByPlace[placeId] = (counterSnap.data()?.orderDisplayNumber as number) ?? 0
  await backfillOpenBillOrders(placeId)
  emitLiveOrders(placeId)
  bindOrderListeners(placeId, startIso)
}

export async function fetchOrdersForIsoRange(startIso: string, endIso: string): Promise<Order[]> {
  const placeId = getCurrentPlaceId()
  const snap = await getDocs(
    query(ordersRef(placeId), where('datetime', '>=', startIso), where('datetime', '<', endIso)),
  )
  return snap.docs.map((d) => orderFromSnap(d).data)
}

export function setHistoryOrders(rows: Order[]): void {
  replaceHistoryOrders(getCurrentPlaceId(), rows)
  dispatchOrdersUpdated()
}

export async function fetchOrdersForBusinessDay(date: string): Promise<Order[]> {
  const { startIso, endIso } = businessDayIsoBounds(date)
  return fetchOrdersForIsoRange(startIso, endIso)
}

export async function fetchOrdersForMonth(monthKey: string): Promise<Order[]> {
  const { startIso, endIso } = monthKeyBounds(monthKey)
  return fetchOrdersForIsoRange(startIso, endIso)
}

export function dropOrderHistoryOverlays(): void {
  const placeId = getCurrentPlaceId()
  if (!historyOrdersByPlace[placeId] || historyOrdersByPlace[placeId].size === 0) return
  historyOrdersByPlace[placeId] = new Map()
  dispatchOrdersUpdated()
}

export function getOrders(): Order[] {
  const placeId = getCurrentPlaceId()
  return ordersCacheByPlace[placeId] ?? []
}

export function getHistoryOrders(): Order[] {
  const placeId = getCurrentPlaceId()
  return [...(historyOrdersByPlace[placeId]?.values() ?? [])]
}

export function getOrdersWithHistory(): Order[] {
  return mergeOrdersById(getHistoryOrders(), getOrders())
}

export function getOrder(id: string): Order | undefined {
  const placeId = getCurrentPlaceId()
  const live = (ordersCacheByPlace[placeId] ?? []).find((o) => o.id === id)
  if (live) return live
  return historyOrdersByPlace[placeId]?.get(id)
}

export async function getNextDisplayNumber(): Promise<string> {
  const placeId = getCurrentPlaceId()
  const db = getFirestoreInstance()
  const next = await runTransaction(db, async (tx) => {
    const counterRef = countersOrdersRef(placeId)
    const snap = await tx.get(counterRef)
    const current = (snap.data()?.orderDisplayNumber as number) ?? 0
    const value = current + 1
    tx.set(counterRef, { orderDisplayNumber: value })
    return value
  })
  counterCacheByPlace[placeId] = next
  return String(next).padStart(3, '0')
}

/** Upsert the given orders only. Never deletes docs missing from a scoped cache. */
export function saveOrders(orders: Order[]): void {
  const placeId = getCurrentPlaceId()
  const pending = getPendingMap(placeId)
  const prev = ordersCacheByPlace[placeId] ?? []
  const byId = new Map(prev.map((o) => [o.id, o]))
  for (const order of orders) byId.set(order.id, order)
  ordersCacheByPlace[placeId] = [...byId.values()]
  dispatchOrdersUpdated()
  ;(async () => {
    const cache = ordersCacheByPlace[placeId]
    for (const order of orders) {
      const latest = resolveOrderForPersist(order, cache, pending)
      const sanitized = stripUndefined(latest)
      await setDoc(
        orderDoc(placeId, order.id),
        { ...sanitized, [LIVE_UPDATED_AT_FIELD]: serverTimestamp() },
        { merge: true },
      )
    }
  })().catch((e) => console.error('Firestore saveOrders failed', e))
}

export function addOrder(order: Order): Order[] {
  const placeId = getCurrentPlaceId()
  const cache = ordersCacheByPlace[placeId] ?? []
  const full: Order = stripUndefined(order)
  ordersCacheByPlace[placeId] = [...cache, full]
  setDoc(orderDoc(placeId, order.id), { ...full, [LIVE_UPDATED_AT_FIELD]: serverTimestamp() }).catch(
    (e) => console.error('Firestore addOrder failed', e),
  )
  dispatchOrdersUpdated()
  return ordersCacheByPlace[placeId]
}

function mergeOrderPatch(existing: Order, patch: Partial<Order>): Order {
  const next = { ...existing, ...patch }
  if (patch.boardHiddenAt === null) delete next.boardHiddenAt
  if (patch.boardAutoHideStartedAt === null) {
    delete next.boardAutoHideStartedAt
    delete next.boardAutoHideStartedServerAt
  }
  if (patch.boardAutoHideStartedServerAt === null) delete next.boardAutoHideStartedServerAt
  if (patch.stationProcessing === undefined && 'stationProcessing' in patch) {
    delete next.stationProcessing
  }
  return next
}

function firestorePatchFromOrderPatch(patch: Partial<Order>): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue
    if (value === null) {
      result[key] = deleteField()
      continue
    }
    if (Array.isArray(value) || (value && typeof value === 'object')) {
      result[key] = stripUndefined(value as object)
    } else {
      result[key] = value
    }
  }
  return result
}

export function updateOrder(id: string, patch: Partial<Order>): Order[] {
  const placeId = getCurrentPlaceId()
  const cache = ordersCacheByPlace[placeId] ?? []
  const now = Date.now()
  const pending = getPendingMap(placeId)
  pending.set(id, mergePendingPatches(pending.get(id), patch, now))

  const liveIdx = cache.findIndex((o) => o.id === id)
  if (liveIdx >= 0) {
    ordersCacheByPlace[placeId] = cache.map((o) => (o.id === id ? mergeOrderPatch(o, patch) : o))
  } else {
    const hist = historyMap(placeId)
    const existing = hist.get(id)
    if (existing) hist.set(id, mergeOrderPatch(existing, patch))
  }
  const found = getOrder(id)
  if (found) {
    const sanitizedPatch = firestorePatchFromOrderPatch(patch)
    if (typeof patch.boardAutoHideStartedAt === 'string' && patch.boardAutoHideStartedAt.trim()) {
      sanitizedPatch.boardAutoHideStartedServerAt = serverTimestamp()
    }
    if (patch.boardAutoHideStartedAt === null) {
      sanitizedPatch.boardAutoHideStartedServerAt = deleteField()
    }
    if (Object.keys(sanitizedPatch).length > 0) {
      sanitizedPatch[LIVE_UPDATED_AT_FIELD] = serverTimestamp()
      updateDoc(orderDoc(placeId, id), sanitizedPatch)
        .catch((e) => {
          console.error('Firestore updateOrder failed', e)
          pending.delete(id)
        })
    }
  }
  dispatchOrdersUpdated()
  return ordersCacheByPlace[placeId]
}

/**
 * Write one station's print acknowledgement without replacing the rest of `printAcks`.
 * Two station tablets can acknowledge the same order concurrently this way.
 */
export function setOrderPrintAck(orderId: string, stationId: string, ack: PrintAck): Order[] {
  const placeId = getCurrentPlaceId()
  const cache = ordersCacheByPlace[placeId] ?? []
  ordersCacheByPlace[placeId] = cache.map((o) => {
    if (o.id !== orderId) return o
    return { ...o, printAcks: { ...o.printAcks, [stationId]: ack } }
  })
  dispatchOrdersUpdated()
  const sanitized = stripUndefined(ack)
  updateDoc(orderDoc(placeId, orderId), {
    [`printAcks.${stationId}`]: sanitized,
    [LIVE_UPDATED_AT_FIELD]: serverTimestamp(),
  }).catch((e) => console.error('Firestore setOrderPrintAck failed', e))
  return ordersCacheByPlace[placeId]
}

function patchOrderPrintClaimCache(
  placeId: string,
  orderId: string,
  slotKey: string,
  claim: PrintClaim | null,
): void {
  ordersCacheByPlace[placeId] = (ordersCacheByPlace[placeId] ?? []).map((o) => {
    if (o.id !== orderId) return o
    const printClaims = { ...o.printClaims }
    if (claim) printClaims[slotKey] = claim
    else delete printClaims[slotKey]
    return { ...o, printClaims }
  })
}

function overlayPrintClaim(placeId: string, orderId: string, slotKey: string, claim: PrintClaim): void {
  const pending = getPendingMap(placeId)
  pending.set(
    orderId,
    mergePendingPatches(pending.get(orderId), { printClaims: { [slotKey]: claim } }, Date.now()),
  )
  patchOrderPrintClaimCache(placeId, orderId, slotKey, claim)
}

function revertPrintClaimOverlay(
  placeId: string,
  orderId: string,
  slotKey: string,
  previous: PrintClaim | undefined,
): void {
  const pending = getPendingMap(placeId)
  const next = removePrintClaimFromPendingPatch(pending.get(orderId), slotKey)
  if (next) pending.set(orderId, next)
  else pending.delete(orderId)
  patchOrderPrintClaimCache(placeId, orderId, slotKey, previous ?? null)
}

/**
 * Mark an on-process slot as taken before the print job record exists.
 * Returns false when a live claim already holds the slot (including this tablet's prior send).
 */
export async function claimOrderPrintSlot(
  orderId: string,
  slotKey: string,
  claim: PrintClaim,
  serverNowMs: number = Date.now(),
): Promise<boolean> {
  const placeId = getCurrentPlaceId()
  if (!placeId || !orderId.trim() || !slotKey.trim()) return false
  const cached = (ordersCacheByPlace[placeId] ?? []).find((o) => o.id === orderId)
  const existing = cached?.printClaims?.[slotKey]
  if (!decidePrintClaimWin(existing, claim, serverNowMs)) return false

  overlayPrintClaim(placeId, orderId, slotKey, claim)
  dispatchOrdersUpdated()

  try {
    const db = getFirestoreInstance()
    const ref = orderDoc(placeId, orderId)
    const won = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref)
      if (!snap.exists()) return false
      const remote = (snap.data() as Order).printClaims?.[slotKey]
      if (!decidePrintClaimWin(remote, claim, serverNowMs)) return false
      tx.update(ref, {
        [`printClaims.${slotKey}`]: {
          ...stripUndefined({
            at: claim.at,
            byDeviceId: claim.byDeviceId,
            claimId: claim.claimId,
          }),
          serverAt: serverTimestamp(),
        },
        [LIVE_UPDATED_AT_FIELD]: serverTimestamp(),
      })
      return true
    })
    if (!won) {
      revertPrintClaimOverlay(placeId, orderId, slotKey, existing)
      dispatchOrdersUpdated()
    }
    return won
  } catch (e) {
    console.warn('Firestore claimOrderPrintSlot failed; not printing', e)
    revertPrintClaimOverlay(placeId, orderId, slotKey, existing)
    dispatchOrdersUpdated()
    return false
  }
}

export async function clearOrderPrintClaim(orderId: string, slotKey: string): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (!placeId || !orderId.trim() || !slotKey.trim()) return
  revertPrintClaimOverlay(placeId, orderId, slotKey, undefined)
  dispatchOrdersUpdated()
  try {
    await updateDoc(orderDoc(placeId, orderId), {
      [`printClaims.${slotKey}`]: deleteField(),
      [LIVE_UPDATED_AT_FIELD]: serverTimestamp(),
    })
  } catch (e) {
    console.error('Firestore clearOrderPrintClaim failed', e)
  }
}

export function deleteOrder(id: string): Order[] {
  const placeId = getCurrentPlaceId()
  const cache = ordersCacheByPlace[placeId] ?? []
  ordersCacheByPlace[placeId] = cache.filter((o) => o.id !== id)
  historyOrdersByPlace[placeId]?.delete(id)
  getPendingMap(placeId).delete(id)
  deleteDoc(orderDoc(placeId, id)).catch((e) =>
    console.error('Firestore deleteOrder failed', e),
  )
  dispatchOrdersUpdated()
  return ordersCacheByPlace[placeId]
}

export function isOrdersPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in ordersCacheByPlace
}

/** Clear in-memory caches and unsubscribe from real-time listener. */
export function clearOrdersCache(): void {
  stopOrderListeners()
  subscribedLiveStartIso = ''
  Object.keys(ordersCacheByPlace).forEach((k) => delete ordersCacheByPlace[k])
  Object.keys(counterCacheByPlace).forEach((k) => delete counterCacheByPlace[k])
  Object.keys(pendingPatchesByPlace).forEach((k) => delete pendingPatchesByPlace[k])
  Object.keys(historyOrdersByPlace).forEach((k) => delete historyOrdersByPlace[k])
}

export function subscribedOrdersLiveStartIso(): string {
  return subscribedLiveStartIso
}

if (typeof window !== 'undefined') {
  window.addEventListener('wortelogy-bills-updated', () => {
    void refreshOpenBillOrderBackfill()
  })
}
